import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import Header from './components/Header';
import ProgressBar from './components/ProgressBar';
import Step1Participation from './components/Step1Participation';
import Step2Details from './components/Step2Details';
import Step3Consent from './components/Step3Consent';
import Step4Confirmation from './components/Step4Confirmation';
import Footer from "../components/Footer.tsx";
import { Form2Data } from './types';
import './App.css';

// Define the shape of the initial data on the window object
declare global {
    interface Window {
        initialData?: Partial<Form2Data>;
    }
}

interface AppProps {
    apiEndpoint?: string;
}

const App: React.FC<AppProps> = ({ apiEndpoint }) => {
    const { token } = useParams<{ token: string }>();
    const [currentStep, setCurrentStep] = useState<number>(1);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submissionError, setSubmissionError] = useState<string | null>(null);
    
    const [formData, setFormData] = useState<Form2Data>(() => {
        const initialData = window.initialData || {};
        const sanitizedInitialData: Partial<Form2Data> = {};

        const validAnrede = ['frau', 'herr', 'divers'];

        // Only use initial data fields that are not null, undefined, or empty strings
        for (const key in initialData) {
            if (Object.prototype.hasOwnProperty.call(initialData, key)) {
                const value = initialData[key as keyof Form2Data];

                if (key === 'anrede') {
                    if (typeof value === 'string' && validAnrede.includes(value)) {
                        sanitizedInitialData.anrede = value;
                    }
                } else if (key === 'standort') {
                    if (typeof value === 'string' && value !== '') {
                        sanitizedInitialData.standort = value;
                    }
                } else if (value !== null && value !== undefined && value !== '') {
                    (sanitizedInitialData as any)[key] = value;
                }
            }
        }

        const baseData = {
            teilnahme: null,
            anrede: '',
            titel: '',
            vorname: '',
            nachname: '',
            email: '',
            standort: '',
            telefonnummer: '',
            zimmer: null,
            shuttle: null,
            ankunftZeit: '',
            ankunftBahnhof: '',
            unvertraeglichkeiten: '',
            bemerkungen: '',
            einverstaendnisDaten: false,
            einverstaendnisFotos: false,
            zustimmungDatenschutz: false,
        };
        
        return { ...baseData, ...sanitizedInitialData };
    });

    const submitData = async (data: Form2Data) => {
        setIsSubmitting(true);
        setSubmissionError(null);
        try {
            const endpoint = apiEndpoint || `/api/forms/registration/token/${token}`;
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
            });

            if (!response.ok) {
                throw new Error('Form submission failed');
            }
            setCurrentStep(4);
        } catch (error: any) {
            console.error('Form submission error:', error);
            setSubmissionError('Ihre Anmeldung konnte nicht übermittelt werden. Bitte versuchen Sie es später erneut.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleStep1Submit = (data: Partial<Form2Data>) => {
        const updatedFormData = { ...formData, ...data };
        setFormData(updatedFormData);
        if (data.teilnahme === 'nein') {
            submitData(updatedFormData);
        } else {
            setCurrentStep(2);
        }
    };

    const handleStep2Submit = (data: Partial<Form2Data>) => {
        setFormData(prev => ({ ...prev, ...data }));
        setCurrentStep(3);
    };

    const handleStep3Submit = async (data: Partial<Form2Data>) => {
        const finalData = { ...formData, ...data };
        setFormData(finalData);
        await submitData(finalData);
    };

    const handleBack = () => {
        setCurrentStep(prev => prev - 1);
    };

    const renderStep = () => {
        switch (currentStep) {
            case 1:
                return <Step1Participation onSubmit={handleStep1Submit} initialData={formData} isSubmitting={isSubmitting} submissionError={submissionError} />;
            case 2:
                return <Step2Details onSubmit={handleStep2Submit} onBack={handleBack} initialData={formData} />;
            case 3:
                return <Step3Consent
                    onSubmit={handleStep3Submit}
                    onBack={handleBack}
                    initialData={formData}
                    isSubmitting={isSubmitting}
                    submissionError={submissionError}
                />;
            case 4:
                return <Step4Confirmation teilnahme={formData.teilnahme} />;
            default:
                return <Step1Participation onSubmit={handleStep1Submit} initialData={formData} isSubmitting={isSubmitting} submissionError={submissionError} />;
        }
    };

    return (
        <div className="app-container">
            <Header />
            <main className="flex-grow container mx-auto p-4 md:p-8">
                <div className="max-w-4xl mx-auto mb-8">
                    {currentStep < 4 && <ProgressBar currentStep={currentStep} />}
                </div>
                {renderStep()}
            </main>
            <Footer />
        </div>
    );
};

export default App;
